<template>
  <div>
    <v-card class="mb-4" elevation="0">
      <div class="d-flex justify-space-between align-center">
        <div>
          <v-card-title class="text-h6 font-weight-bold">
            지금은 <span class="text-orange-darken-2">{{ currentYear }}년</span>이에요
          </v-card-title>
          
        </div>
        <!-- Year selection removed as per previous request -->
      </div>
    </v-card>

    <!-- 1. This Year's New Year (Small Card) -->
    <v-card 
      v-if="thisYearNewYear"
      class="mb-4"
      variant="outlined"
      border
      rounded="lg"
      @click="openEventDetails(thisYearNewYear)"
    >
      <v-card-item>
        <div class="d-flex justify-space-between align-center mb-1">
          <v-chip size="x-small" label color="grey-darken-1" variant="flat">
            {{ currentYear }}년 신정
          </v-chip>
          <span class="text-caption text-medium-emphasis font-weight-bold">
            {{ calculateDDay(thisYearNewYear.date) }}
          </span>
        </div>
        <v-card-title class="text-subtitle-1 font-weight-bold text-wrap pt-0" style="line-height: 1.2; word-break: keep-all;">
          {{ thisYearNewYear.title }}
        </v-card-title>
        <v-card-subtitle class="text-caption d-flex align-center">
          <v-icon icon="mdi-calendar-check" size="x-small" class="mr-1"></v-icon>
          {{ formatDate(thisYearNewYear.date) }}
        </v-card-subtitle>
      </v-card-item>
    </v-card>

    <!-- 2. Next Year's New Year (Main Large Card - Dashboard Style) -->
    <v-card 
      class="mb-6 overflow-hidden"
      elevation="2"
      border
      rounded="xl"
      color="orange-darken-2"
      variant="flat"
      @click="openEventDetails(nextYearNewYear)"
    >
      <v-row no-gutters>
        <!-- Left: Info -->
        <v-col cols="12" sm="8" class="pa-4 pa-sm-6">
          <div class="text-overline text-orange-lighten-4 font-weight-bold mb-1">NEXT NEW YEAR</div>
          <div class="text-h4 text-sm-h3 font-weight-black text-white mb-1 mb-sm-2">
            {{ calculateDDay(nextYearNewYear.date) }}
          </div>
          <div class="text-subtitle-1 text-sm-h5 font-weight-bold text-white mb-1 mb-sm-2 text-wrap" style="word-break: keep-all;">
            {{ nextYearNewYear.title }}
          </div>
          <div class="d-flex align-center text-caption text-sm-subtitle-1 text-orange-lighten-4">
            <v-icon icon="mdi-calendar-star" size="x-small" class="mr-1 mr-sm-2"></v-icon>
            {{ formatDate(nextYearNewYear.date) }}
          </div>
        </v-col>
        
        <!-- Right: Icon -->
        <v-col cols="12" sm="4" class="d-flex align-center justify-center pa-3 pa-sm-6 bg-orange-darken-3 border-sm-left">
          <div class="d-flex flex-row flex-sm-column align-center">
            <v-icon icon="mdi-party-popper" size="32" size-sm="64" color="orange-lighten-4" class="mr-3 mr-sm-0 mb-sm-2"></v-icon>
            <div class="text-subtitle-2 text-sm-h6 font-weight-bold text-white text-center">
              오늘 하루도<br class="d-none d-sm-block"> 힘내봐요!
            </div>
          </div>
        </v-col>
      </v-row>
    </v-card>

    <!-- 3. Progress Gauge (Year Progress) -->
    <v-card class="mb-8 pa-4 pa-sm-5" border elevation="1" rounded="lg">
      <div class="d-flex justify-space-between align-end mb-2">
        <div>
          <div class="text-body-2 text-sm-subtitle-1 font-weight-bold text-grey-darken-3">{{ currentYear }}년 진행률</div>
        
        </div>
        <div class="text-h6 text-sm-h5 font-weight-black text-orange-darken-2">
          {{ yearProgressPercentage }}%
        </div>
      </div>
      <v-progress-linear
        :model-value="yearProgressPercentage"
        color="orange-darken-2"
        height="8"
        height-sm="12"
        rounded
        bg-color="orange-lighten-4"
      >
      </v-progress-linear>
      
      <!-- Detailed Remaining Time (Grid) -->
      <v-row class="mt-4 text-center" dense>
        <v-col cols="6" sm="3" class="mb-2 mb-sm-0">
          <div class="bg-grey-lighten-4 rounded py-2">
            <div class="text-h6 font-weight-bold text-orange-darken-2">{{ remainingWeekdays }}일</div>
            <div class="text-caption text-medium-emphasis">남은 평일</div>
          </div>
        </v-col>
        <v-col cols="6" sm="3" class="mb-2 mb-sm-0">
          <div class="bg-grey-lighten-4 rounded py-2">
            <div class="text-h6 font-weight-bold text-orange-darken-2">{{ remainingWeekends }}일</div>
            <div class="text-caption text-medium-emphasis">남은 주말/휴일</div>
          </div>
        </v-col>
        <v-col cols="6" sm="3">
          <div class="bg-grey-lighten-4 rounded py-2">
            <div class="text-h6 font-weight-bold text-orange-darken-2">{{ remainingHours }}시간</div>
            <div class="text-caption text-medium-emphasis">남은 시간</div>
          </div>
        </v-col>
        <v-col cols="6" sm="3">
          <div class="bg-grey-lighten-4 rounded py-2">
            <div class="text-h6 font-weight-bold text-orange-darken-2">{{ remainingSeconds }}초</div>
            <div class="text-caption text-medium-emphasis">남은 초</div>
          </div>
        </v-col>
      </v-row>
    </v-card>

    <!-- 4. Other Events List -->
    <div v-if="otherEvents.length > 0" class="d-flex align-center mb-4">
      <v-icon icon="mdi-calendar-text" color="orange-darken-2" class="mr-2"></v-icon>
      <h3 class="text-h6 font-weight-bold text-grey-darken-3">기타 일정</h3>
    </div>

    <v-row>
      <v-col 
        v-for="event in otherEvents" 
        :key="event.id" 
        cols="12" 
        sm="6" 
        md="4"
      >
        <v-card 
          class="h-100" 
          hover 
          elevation="1"
          @click="openEventDetails(event)"
          rounded="lg"
          style="border-top: 4px solid;"
          :style="{ borderTopColor: '#F57C00' }"
        >
          <v-card-item>
            <div class="d-flex justify-space-between align-center mb-2">
              <v-chip color="orange-darken-2" variant="tonal" size="x-small" label class="font-weight-bold">
                새해
              </v-chip>
              <span class="text-subtitle-2 font-weight-bold text-orange-darken-2">{{ calculateDDay(event.date) }}</span>
            </div>
            <v-card-title class="text-h6 font-weight-bold pb-1 text-wrap" style="line-height: 1.2; word-break: keep-all;">{{ event.title }}</v-card-title>
            <div class="d-flex align-center mt-2 text-caption text-medium-emphasis font-weight-medium">
              <v-icon icon="mdi-calendar" size="x-small" class="mr-1"></v-icon>
              {{ formatDate(event.date) }}
            </div>
          </v-card-item>
        </v-card>
      </v-col>
    </v-row>

    <!-- Event Detail Dialog -->
    <v-dialog v-model="dialog" width="500">
      <v-card v-if="selectedEvent">
        <v-card-title class="text-h5 bg-orange-darken-2 text-white">
          {{ selectedEvent.title }}
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="mb-4">
            <v-icon icon="mdi-calendar" class="mr-2"></v-icon>
            <strong>일시:</strong> {{ formatDate(selectedEvent.date) }}
            <span class="ml-2 text-orange-darken-2 font-weight-bold">({{ calculateDDay(selectedEvent.date) }})</span>
          </div>
          <v-divider class="mb-4"></v-divider>
          <div class="text-body-1">
            {{ selectedEvent.description }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="orange-darken-2" variant="text" @click="dialog = false">닫기</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { events } from '../data/events';
import { formatDate, calculateDDay } from '../assets/globalFx';

export default {
  name: 'NewYearPage',
  data() {
    return {
      dialog: false,
      selectedEvent: null,
      events: events,
      currentYear: new Date().getFullYear(),
      now: new Date(),
      timer: null
    }
  },
  mounted() {
    this.timer = setInterval(() => {
      this.now = new Date();
      this.currentYear = this.now.getFullYear();
    }, 50); 
  },
  beforeUnmount() {
    if (this.timer) clearInterval(this.timer);
  },
  computed: {
    filteredEvents() {
      return this.events
        .filter(event => {
            const eventYear = new Date(event.date).getFullYear();
            return event.categoryId === 'newyear' && eventYear === this.currentYear;
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date));
    },
    thisYearNewYear() {
      const existing = this.filteredEvents.find(e => e.title.includes('신정'));
      if (existing) return existing;
      
      return {
        id: `virtual-newyear-${this.currentYear}`,
        title: `${this.currentYear}년 신정`,
        date: `${this.currentYear}-01-01`,
        description: `${this.currentYear}년 새해 첫날입니다.`
      };
    },
    nextYearNewYear() {
      const nextYear = this.currentYear + 1;
      const existing = this.events.find(e => 
        e.categoryId === 'newyear' && 
        e.title.includes('신정') && 
        new Date(e.date).getFullYear() === nextYear
      );

      if (existing) return existing;

      return {
        id: `virtual-newyear-${nextYear}`,
        title: `${nextYear}년 신정`,
        date: `${nextYear}-01-01`,
        description: `${nextYear}년 희망찬 새해가 밝았습니다.`
      };
    },
    otherEvents() {
      return this.filteredEvents.filter(e => !e.title.includes('신정'));
    },
    yearProgressPercentage() {
      const startOfYear = new Date(this.currentYear, 0, 1);
      const endOfYear = new Date(this.currentYear + 1, 0, 1);
      const totalDuration = endOfYear - startOfYear;
      const passedDuration = this.now - startOfYear;
      
      const percentage = (passedDuration / totalDuration) * 100;
      return Math.min(Math.max(percentage, 0), 100).toFixed(8);
    },
    endOfYear() {
        return new Date(this.currentYear + 1, 0, 1);
    },
    remainingSeconds() {
        const diff = Math.max(0, this.endOfYear - this.now);
        return Math.floor(diff / 1000).toLocaleString();
    },
    remainingHours() {
        const diff = Math.max(0, this.endOfYear - this.now);
        return Math.floor(diff / (1000 * 60 * 60)).toLocaleString();
    },
    remainingWeekdays() {
        let count = 0;
        let d = new Date(this.now);
        d.setHours(0,0,0,0);
        const end = new Date(this.endOfYear);
        end.setHours(0,0,0,0);

        // 오늘부터 계산 로직 유지 (기존 코드와 동일)
        const holidaySet = new Set(
          this.events
            .filter(e => (e.categoryId === 'holiday' || e.categoryId === 'newyear') && new Date(e.date).getFullYear() === this.currentYear)
            .map(e => e.date)
        );

        while (d < end) {
            const dateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
            const day = d.getDay();
            const isWeekend = day === 0 || day === 6;
            const isHoliday = holidaySet.has(dateStr);

            if (!isWeekend && !isHoliday) {
                count++;
            }
            d.setDate(d.getDate() + 1);
        }
        return count;
    },
    remainingWeekends() {
        let count = 0;
        let d = new Date(this.now);
        d.setHours(0,0,0,0);
        const end = new Date(this.endOfYear);
        end.setHours(0,0,0,0);
        
        const holidaySet = new Set(
          this.events
            .filter(e => (e.categoryId === 'holiday' || e.categoryId === 'newyear') && new Date(e.date).getFullYear() === this.currentYear)
            .map(e => e.date)
        );

        while (d < end) {
            const dateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
            const day = d.getDay();
            const isWeekend = day === 0 || day === 6;
            const isHoliday = holidaySet.has(dateStr);

            if (isWeekend || isHoliday) {
                count++;
            }
            d.setDate(d.getDate() + 1);
        }
        return count;
    }
  },
  methods: {
    formatDate,
    calculateDDay,
    openEventDetails(event) {
      this.selectedEvent = event;
      this.dialog = true;
    }
  }
}
</script>
